/**
 * Database Blueprint — Setup dialog (Slice 1).
 *
 * Wizard for one-shot DDL export of a single (connection + database) to a
 * folder on disk. Drives `electronAPI.databaseBlueprint.setup` and reports the
 * resulting object counts on success.
 *
 * Multi-connection setup arrives in Slice 7; this dialog reuses the same
 * shape but is single-connection only for now.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Database, FolderOpen, Link2, ListFilter, ListTree, Table2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/components/ui/toaster';
import { DocsLinkButton } from '@/features/help-feedback';
import { cn } from '@/lib/utils';
import {
  DEFAULT_MIRROR_OBJECT_TYPES,
  type BlueprintExcludedObject,
  type BlueprintExcludedObjectKind,
  type BlueprintObjectTypes,
} from '../types/databaseBlueprintTypes';
import { useDatabaseBlueprintSetupStore } from '../store/databaseBlueprintSetupStore';
import { ExclusionPickerDialog } from './ExclusionPickerDialog';
import { LinkedDatabasesPickerDialog } from './LinkedDatabasesPickerDialog';
import { ObjectTypesPickerDialog } from './ObjectTypesPickerDialog';
import { SchemasPickerDialog } from './SchemasPickerDialog';
import { BlueprintDataPickerDialog } from './BlueprintDataPickerDialog';
import {
  abortBlueprintSetup,
  folderIsEmpty,
  listBlueprintObjects,
  listSourceObjects,
  listUserSchemas,
  pickBlueprintFolder,
  readBlueprintMetadata,
  setupBlueprintWithProgress,
  updateBlueprintSettings,
} from '../services/databaseBlueprintApi';
import { useBlueprintStateStore } from '../../workspace/database-blueprint/store/blueprintStateStore';
import { resolveActiveLinkLabel } from '../utils/activeLink';
import { exclusionRowKey } from '../utils/exclusionKey';
import { useScriptBrowserStore } from '../../script-browser/store/scriptBrowserStore';
import { useMountedFolderIndexStore } from '../../script-browser/store/mountedFolderIndexStore';
import { useRefreshFromDbDialogStore } from '../../workspace/database-blueprint/store/refreshFromDbDialogStore';
import { openDatabaseBlueprintTab } from '../services/openDatabaseBlueprintTab';
import { designTableAction } from '../../shared/object-actions/actions/designActions';
import { useProFeature } from '../../../core/licensing';
import { UpgradeDialog } from '../../../core/licensing/components/UpgradeDialog';
import { isProRequiredError } from '../../../core/licensing/types';
import { useProUpgradePromptStore } from '../../../core/licensing/store/proUpgradePromptStore';
import { PERSONAL_MODE_MAX_OBJECTS } from '../../workspace/schema-compare/utils/personalModeLimits';
import { Sparkles, AlertTriangle } from 'lucide-react';
import {
  engineDisplayName,
  engineHasSchemas,
  sanitizeDefaultBlueprintLabel,
} from '../utils/engineDisplayName';
import { buildSelectionSummary } from '../utils/selectionSummary';
import { encodeTableKey, decodeTableKey } from '../utils/tableKey';
import {
  BlueprintCreationProgressDialog,
  type BlueprintCreationLogEntry,
  type BlueprintCreationStatus,
  type BlueprintCreationSummary,
} from './BlueprintCreationProgressDialog';
import type { BlueprintSetupPhase } from '../types/databaseBlueprintTypes';

type CreationProgressState = {
  open: boolean;
  status: BlueprintCreationStatus;
  phase: BlueprintSetupPhase | null;
  log: BlueprintCreationLogEntry[];
  currentIndex: number | null;
  totalCount: number | null;
  summary: BlueprintCreationSummary | null;
  errorMessage: string | null;
  operationId: string | null;
  /** Folder id from `markFolderAsBlueprint`, set when status === 'done'. */
  pendingFolderId: string | null;
  /** Label to pass to `openDatabaseBlueprintTab` on done. */
  pendingLabel: string;
};

const INITIAL_CREATION_PROGRESS: CreationProgressState = {
  open: false,
  status: 'running',
  phase: null,
  log: [],
  currentIndex: null,
  totalCount: null,
  summary: null,
  errorMessage: null,
  operationId: null,
  pendingFolderId: null,
  pendingLabel: '',
};

/**
 * Human-friendly labels for each `BlueprintObjectTypes` key — blueprints the labels
 * shown in `ObjectTypeCheckboxList`. Used by the dialog's summary row so it
 * reads as "Selected: Tables, Views" instead of "Selected: tables, views".
 */
const OBJECT_TYPE_LABELS: Record<string, string> = {
  tables: 'Tables',
  views: 'Views',
  procedures: 'Stored procedures',
  functions: 'Functions',
  triggers: 'Triggers',
  userTypes: 'User-defined types',
  sequences: 'Sequences',
  synonyms: 'Synonyms',
  permissions: 'Permissions',
  schemas: 'Schemas',
  packages: 'Packages',
};

function generateOperationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `blueprint-setup-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function DatabaseBlueprintSetupDialog() {
  const { isOpen, mode, source, editContext, closeSetupDialog } =
    useDatabaseBlueprintSetupStore();
  const isEditMode = mode === 'edit' && editContext !== null;
  const updateBlueprintMetadata = useScriptBrowserStore((s) => s.updateBlueprintMetadata);

  // Local state — reset whenever the dialog opens.
  const [folderPath, setFolderPath] = useState('');
  const [label, setLabel] = useState('');
  const [includedObjectTypes, setIncludedObjectTypes] = useState<BlueprintObjectTypes>(
    DEFAULT_MIRROR_OBJECT_TYPES
  );
  const [includeSystemSchemas, setIncludeSystemSchemas] = useState(false);
  const [writeGitignore, setWriteGitignore] = useState(true);
  const [availableSchemas, setAvailableSchemas] = useState<string[]>([]);
  const [selectedSchemas, setSelectedSchemas] = useState<Set<string>>(new Set());
  const [schemasLoading, setSchemasLoading] = useState(false);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [objectCount, setObjectCount] = useState<number | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  // Edit-mode exclusion state — populated from the candidate list (sidecar)
  // + pre-ticked from `editContext.metadata.excludedObjects`.
  const [exclusionCandidates, setExclusionCandidates] = useState<
    Array<{
      kind: 'table' | 'view' | 'procedure' | 'function';
      schema: string;
      name: string;
    }>
  >([]);
  /** Last error from the object-list loader (blueprint-folder parse failure in
   *  edit mode, or live-source query failure in new mode). Surfaced to the
   *  Data Blueprint / Exclusion pickers so an empty list is never silently
   *  attributed to "no DB connection". */
  const [exclusionCandidatesError, setExclusionCandidatesError] = useState<
    string | null
  >(null);
  const [exclusionKeys, setExclusionKeys] = useState<Set<string>>(new Set());
  const [exclusionPickerOpen, setExclusionPickerOpen] = useState(false);
  // Table data state — tables the user opted to also dump as `.data.sql`
  // upsert scripts alongside their DDL files. Keys are encodeTableKey({schema, name}).
  const [blueprintDataTables, setBlueprintDataTables] = useState<Set<string>>(new Set());
  // Subset of `blueprintDataTables` for which the user has explicitly opted into
  // destructive DELETE+INSERT replay. Only meaningful for no-PK tables — the
  // emitter ignores the flag on PK-bearing tables.
  const [blueprintDataDestructive, setBlueprintDataDestructive] = useState<Set<string>>(new Set());
  const [blueprintDataPickerOpen, setBlueprintDataPickerOpen] = useState(false);
  // Sub-dialogs for the new compact "Change…" rows.
  const [objectTypesPickerOpen, setObjectTypesPickerOpen] = useState(false);
  const [schemasPickerOpen, setSchemasPickerOpen] = useState(false);
  const [linkedDbsPickerOpen, setLinkedDbsPickerOpen] = useState(false);
  /** Snapshot of linkedDbs for the edit-mode summary row. Seeded from the
   *  initial editContext.metadata.linkedDbs and refreshed via
   *  `readBlueprintMetadata` after the picker reports a change (the picker
   *  mutates state through the script-browser store + IPCs that don't flow
   *  back into editContext on their own). */
  const [linkedDbsSnapshot, setLinkedDbsSnapshot] = useState<
    ReadonlyArray<{
      label: string;
      connectionId: string;
      connectionDisplayName: string;
      database: string;
      engine: import('../types/databaseBlueprintTypes').UserFacingEngineId;
      lastSyncAt: string | null;
    }>
  >([]);
  /** Current active link label tracked alongside the snapshot. */
  const [activeLinkLabelSnapshot, setActiveLinkLabelSnapshot] = useState<string>('');
  const [creationProgress, setCreationProgress] = useState<CreationProgressState>(
    INITIAL_CREATION_PROGRESS,
  );
  const { canUse: canBlueprintLarge } = useProFeature('database-blueprint-large');
  const showUpgradePrompt = useProUpgradePromptStore((s) => s.showPrompt);

  // Reset on open
  useEffect(() => {
    if (!isOpen || !source) return;
    setCreationProgress(INITIAL_CREATION_PROGRESS);
    if (isEditMode && editContext) {
      // Edit mode — pre-populate from the existing blueprint metadata.
      setFolderPath(editContext.blueprintFolderPath);
      const firstLink = editContext.metadata.linkedDbs[0];
      setLabel(firstLink?.label ?? sanitizeDefaultBlueprintLabel(source.database));
      setLinkedDbsSnapshot(editContext.metadata.linkedDbs);
      setActiveLinkLabelSnapshot(
        resolveActiveLinkLabel(editContext.metadata) ?? '',
      );
      setLinkedDbsPickerOpen(false);
      setIncludedObjectTypes(editContext.metadata.includedObjectTypes);
      setIncludeSystemSchemas(false);
      setWriteGitignore(true);
      setAvailableSchemas(editContext.metadata.includedSchemas);
      setSelectedSchemas(new Set(editContext.metadata.includedSchemas));
      setFolderError(null);
      setSubmitting(false);
      setObjectCount(null);
      setShowUpgradeDialog(false);
      const initialExclusions =
        editContext.metadata.excludedObjects ?? [];
      // P2 — keyed via the shared NUL-delimited encoder so space-bearing
      // identifiers don't collide on submit (legacy `${kind} ${schema}
      // ${name}` collapsed `('table','a b','c')` and `('table','a','b c')`
      // to a single key).
      setExclusionKeys(new Set(initialExclusions.map(exclusionRowKey)));
      setExclusionPickerOpen(false);
      setObjectTypesPickerOpen(false);
      setSchemasPickerOpen(false);
      setBlueprintDataPickerOpen(false);
      const persistedBlueprintData = editContext.metadata.blueprintDataTables ?? [];
      setBlueprintDataTables(
        new Set(persistedBlueprintData.map((t) => encodeTableKey(t))),
      );
      setBlueprintDataDestructive(
        new Set(
          persistedBlueprintData
            .filter((t) => t.allowDestructiveReplay === true)
            .map((t) => encodeTableKey(t)),
        ),
      );
    } else {
      setFolderPath('');
      // Finding #22c — default label is a sanitised env-name derived from the
      // database name (no `${engine}-` prefix, no spaces). Empty for databases
      // whose name has nothing label-safe.
      setLabel(sanitizeDefaultBlueprintLabel(source.database));
      setIncludedObjectTypes(DEFAULT_MIRROR_OBJECT_TYPES);
      setIncludeSystemSchemas(false);
      setWriteGitignore(true);
      setAvailableSchemas([]);
      setSelectedSchemas(new Set());
      setFolderError(null);
      setSubmitting(false);
      setObjectCount(null);
      setShowUpgradeDialog(false);
      setExclusionCandidates([]);
      setExclusionCandidatesError(null);
      setExclusionKeys(new Set());
      setExclusionPickerOpen(false);
      setObjectTypesPickerOpen(false);
      setSchemasPickerOpen(false);
      setBlueprintDataPickerOpen(false);
      setBlueprintDataTables(new Set());
      setBlueprintDataDestructive(new Set());
      setLinkedDbsPickerOpen(false);
      setLinkedDbsSnapshot([]);
      setActiveLinkLabelSnapshot('');
    }
  }, [isOpen, source, isEditMode, editContext]);

  // Fetch the candidate object list for the exclusion + blueprint-data pickers.
  // Always prefer the live source connection (which in edit mode is the
  // currently-selected linked DB): it's the authoritative table list, it
  // matches what data blueprint will actually read from, and it bypasses the
  // folder-side multi-link-unsafe block that fires on a fresh clone with no
  // baseline caches. The folder-listing path is only used as a fallback when
  // no live source is available (rare — `source` is populated by the
  // openSettingsDialog caller in edit mode and by the wizard in new mode).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setExclusionCandidatesError(null);
    const sortFn = (
      a: { kind: string; schema: string; name: string },
      b: { kind: string; schema: string; name: string },
    ): number => {
      const k = a.kind.localeCompare(b.kind);
      if (k !== 0) return k;
      const s = a.schema.localeCompare(b.schema, undefined, {
        sensitivity: 'base',
      });
      if (s !== 0) return s;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    };
    const loader: Promise<
      | {
          success: true;
          objects: Array<{
            kind: 'table' | 'view' | 'procedure' | 'function';
            schema: string;
            name: string;
          }>;
        }
      | { success: false; error: string }
    > = source
      ? listSourceObjects({
          connectionId: source.connectionId,
          database: source.database,
          engine: source.engine,
        })
      : isEditMode && editContext
        ? (() => {
            const linkLabel =
              resolveActiveLinkLabel(editContext.metadata) ?? '';
            return linkLabel
              ? listBlueprintObjects(editContext.blueprintFolderPath, linkLabel)
              : Promise.resolve({ success: true as const, objects: [] });
          })()
        : Promise.resolve({ success: true as const, objects: [] });
    loader
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setExclusionCandidates([...r.objects].sort(sortFn));
          setExclusionCandidatesError(null);
        } else {
          // Surface the loader error so the picker can tell the user WHY the
          // list is empty (e.g. a `.sql` file failed to parse) instead of
          // pretending no tables exist and prompting them to "connect to a
          // source DB first" — which is misleading in edit mode where the
          // connection is fine and the list comes from disk.
          console.error('[DatabaseBlueprint] failed to load object list:', r.error);
          setExclusionCandidates([]);
          setExclusionCandidatesError(r.error);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[DatabaseBlueprint] object-list loader threw:', err);
        setExclusionCandidates([]);
        setExclusionCandidatesError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isEditMode, editContext, source]);

  // Fetch schemas when source / system-schemas toggle changes.
  // In edit mode we still fetch so the user can flip their schema selection
  // against the *current* DB schema list (not just what was saved). The saved
  // selection is preserved across the fetch (any schemas missing from the DB
  // are dropped).
  useEffect(() => {
    if (!isOpen || !source) return;
    let cancelled = false;
    setSchemasLoading(true);
    listUserSchemas(source.connectionId, source.database, source.engine, includeSystemSchemas)
      .then((schemas) => {
        if (cancelled) return;
        setAvailableSchemas(schemas);
        if (isEditMode && editContext) {
          // Preserve the saved selection VERBATIM — union it into the selected
          // set even when a saved schema is missing from the freshly-fetched
          // user-schema list (e.g. a system schema like `information_schema`
          // saved while the system-schemas toggle is off). The previous
          // intersection silently dropped those, so opening Settings and
          // hitting Save pruned them from the manifest and the next Refresh
          // deleted their files. Union keeps the round-trip lossless.
          const saved = editContext.metadata.includedSchemas;
          const next = new Set(schemas.filter((s) => saved.includes(s)));
          for (const s of saved) next.add(s);
          setSelectedSchemas(next);
        } else {
          // Default-select every schema (blueprints plan: all user schemas on by default).
          setSelectedSchemas(new Set(schemas));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error({
          title: 'Could not list schemas',
          description: err instanceof Error ? err.message : String(err),
        });
        setAvailableSchemas([]);
      })
      .finally(() => {
        if (!cancelled) setSchemasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, source, includeSystemSchemas, isEditMode, editContext]);

  // Refresh the object count whenever the source / schema selection /
  // object-type selection changes. The count drives the Pro-mode gate.
  useEffect(() => {
    if (!isOpen || !source || schemasLoading) return;
    let cancelled = false;
    const api = window.electronAPI?.schemaCompare;
    if (!api) return;
    api
      .getObjectCounts({
        connectionId: source.connectionId,
        database: source.database,
        options: {
          includeTables: includedObjectTypes.tables,
          includeViews: includedObjectTypes.views,
          includeProcedures: includedObjectTypes.procedures,
          includeFunctions: includedObjectTypes.functions,
          includeSystemSchemas,
        },
        schemaFilter:
          selectedSchemas.size > 0 && selectedSchemas.size < availableSchemas.length
            ? { mode: 'include', schemas: Array.from(selectedSchemas) }
            : undefined,
      })
      .then((result) => {
        if (cancelled) return;
        if (result.success && result.counts) {
          const total =
            result.counts.tables +
            result.counts.views +
            result.counts.procedures +
            result.counts.functions;
          setObjectCount(total);
        } else {
          setObjectCount(null);
        }
      })
      .catch(() => {
        if (!cancelled) setObjectCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    source,
    schemasLoading,
    includedObjectTypes,
    includeSystemSchemas,
    selectedSchemas,
    availableSchemas.length,
  ]);

  /**
   * Finding #31 — validate emptiness on any path that produces a folder path,
   * not just the native picker. Used by `onBlur` on the folder Input
   * (typed/pasted paths) and by `handleSubmit` (final guard).
   */
  const validateFolderEmptiness = useCallback(
    async (candidatePath: string): Promise<boolean> => {
      if (!candidatePath) return false;
      const emptiness = await folderIsEmpty(candidatePath);
      // The `folder-is-empty` IPC reports a non-empty `error` for cases it
      // can't validate as an empty target — a path that resolves to a FILE
      // (ENOTDIR, where `exists` is false), or a nested blueprint folder. Treat
      // any such error as a hard validation failure and surface it verbatim;
      // otherwise the ENOTDIR case (`empty:false, exists:false`) would slip
      // past the `!empty && exists` guard and let a file path submit.
      if (emptiness.error) {
        setFolderError(emptiness.error);
        return false;
      }
      if (!emptiness.empty && emptiness.exists) {
        setFolderError(
          'The chosen folder is not empty. Pick an empty folder, or create a new one to avoid mixing files.'
        );
        return false;
      }
      return true;
    },
    [],
  );

  const handlePickFolder = useCallback(async () => {
    setFolderError(null);
    const result = await pickBlueprintFolder();
    if (result.cancelled) return;
    if (result.error) {
      setFolderError(result.error);
      return;
    }
    if (result.directoryPath) {
      setFolderPath(result.directoryPath);
      await validateFolderEmptiness(result.directoryPath);
    }
  }, [validateFolderEmptiness]);

  /**
   * Attach an EXISTING blueprint folder (bypasses the empty-folder rule, which
   * is create-only). Mirrors DatabaseBlueprintTab.handleRemount: pick → read
   * manifest → if valid, markFolderAsBlueprint (lenient verbatim linkedDbs) +
   * open tab + close dialog; else inline error.
   */
  const handleAttachExisting = useCallback(async () => {
    setAttachError(null);
    const pick = await pickBlueprintFolder();
    if (pick.cancelled) return;
    if (pick.error) {
      setAttachError(pick.error);
      return;
    }
    if (!pick.directoryPath) return;
    const meta = await readBlueprintMetadata({ folderPath: pick.directoryPath });
    if (!meta.success) {
      setAttachError('Not a blueprint folder');
      return;
    }
    const attachLabel =
      meta.metadata.linkedDbs[0]?.label ??
      pick.directoryPath.split('/').filter(Boolean).pop() ??
      'Blueprint';
    const folderId = useScriptBrowserStore.getState().markFolderAsBlueprint(
      pick.directoryPath,
      attachLabel,
      {
        version: 1,
        linkedDbs: meta.metadata.linkedDbs.map((l) => ({
          label: l.label,
          connectionId: l.connectionId,
          connectionDisplayName: l.connectionDisplayName,
          database: l.database,
          engine: l.engine,
          lastSyncAt: l.lastSyncAt,
        })),
        excludedObjects: meta.metadata.excludedObjects ?? [],
        ...(meta.metadata.activeLinkLabel ? { activeLinkLabel: meta.metadata.activeLinkLabel } : {}),
      },
    );
    window.electronAPI?.analytics
      ?.track('feature:database_blueprint_attach_existing')
      .catch(() => {
        /* fire-and-forget */
      });
    openDatabaseBlueprintTab(folderId, attachLabel);
    closeSetupDialog();
  }, [closeSetupDialog]);

  // Finding #31 — if the user types/pastes a path into the input (no native
  // picker involved), validate emptiness on blur so the same safety net
  // applies. Without this, typed paths bypass the check and `handleSubmit`
  // proceeds to write Tables/Views over whatever was already there.
  const handleFolderInputBlur = useCallback(async () => {
    const trimmed = folderPath.trim();
    if (!trimmed) return;
    await validateFolderEmptiness(trimmed);
  }, [folderPath, validateFolderEmptiness]);

  // Combined list of candidates + already-excluded objects (some excluded
  // entries may not be in the candidate list if the sidecar hasn't been
  // refreshed since they were excluded — keep them visible so users can
  // un-tick).
  const allExclusionRows = useMemo(() => {
    // Edit mode may carry already-excluded entries the sidecar list doesn't
    // surface (e.g. excluded before the sidecar was regenerated) — preserve
    // them so users can un-tick.
    if (!isEditMode || !editContext) return exclusionCandidates;
    const seen = new Set(exclusionCandidates.map(exclusionRowKey));
    const initial = editContext.metadata.excludedObjects ?? [];
    const extras: Array<{
      kind: 'table' | 'view' | 'procedure' | 'function';
      schema: string;
      name: string;
    }> = [];
    for (const e of initial) {
      if (
        (e.kind === 'table' ||
          e.kind === 'view' ||
          e.kind === 'procedure' ||
          e.kind === 'function') &&
        !seen.has(exclusionRowKey(e))
      ) {
        extras.push({ kind: e.kind, schema: e.schema, name: e.name });
      }
    }
    return [...exclusionCandidates, ...extras];
  }, [isEditMode, exclusionCandidates, editContext]);

  // Stable list of {schema, name} table rows for the Data Blueprint picker.
  // Memoised so the picker's effects (count-reset, eager-fetch) don't fire on
  // every parent render — the picker used to nuke its row-count cache when it
  // saw a new array reference, which lost counts every time the user added a
  // table.
  const blueprintDataCandidateRows = useMemo(
    () =>
      allExclusionRows
        .filter((r) => r.kind === 'table')
        .map((r) => ({ schema: r.schema, name: r.name })),
    [allExclusionRows],
  );

  // Tables that were in the saved blueprint-data selection but are no longer
  // ticked in this dialog session. Saving will silently delete their
  // `.data.sql` + `.data.json` files on disk — surface that as an inline
  // warning above the Save button so the user sees the destructive
  // consequence before committing. Computed up here (rather than alongside
  // the other render-time derivations near the bottom of the body) because
  // those happen AFTER an early `if (!source) return null`, and a hook
  // can't sit after a conditional return.
  const blueprintDataTablesToDrop = useMemo(() => {
    if (!isEditMode || !editContext) return [] as Array<{ schema: string; name: string }>;
    const persisted = editContext.metadata.blueprintDataTables ?? [];
    return persisted
      .map((t) => ({ schema: t.schema, name: t.name }))
      .filter((t) => !blueprintDataTables.has(encodeTableKey(t)));
  }, [isEditMode, editContext, blueprintDataTables]);

  // (Filter / move logic lives inside ExclusionPickerDialog; the parent just
  // owns the excluded-keys set so submission can serialise it into the IPC.)

  const handleSubmit = useCallback(async () => {
    if (!source) return;
    // ---------- Edit-mode submit ----------
    if (isEditMode && editContext) {
      setSubmitting(true);
      try {
        const initialExclusions =
          editContext.metadata.excludedObjects ?? [];
        // Build the next exclusions: ticked rows from the picker + any
        // out-of-scope kinds the picker doesn't surface (preserved verbatim).
        const tickedByKey = new Map<string, BlueprintExcludedObject>();
        for (const row of allExclusionRows) {
          const key = exclusionRowKey(row);
          if (exclusionKeys.has(key)) {
            tickedByKey.set(key, {
              kind: row.kind as BlueprintExcludedObjectKind,
              schema: row.schema,
              name: row.name,
            });
          }
        }
        const outOfScope = initialExclusions.filter(
          (e) =>
            e.kind !== 'table' &&
            e.kind !== 'view' &&
            e.kind !== 'procedure' &&
            e.kind !== 'function',
        );
        const nextExclusions: BlueprintExcludedObject[] = [
          ...outOfScope,
          ...tickedByKey.values(),
        ];

        // H5 — when the label input is hidden (multi-link edit mode), do
        // NOT send `label` — the IPC handler would otherwise rename
        // linkedDbs[0] from the seeded value, which is the bug we're fixing.
        const hideLabelOnSubmit =
          editContext.metadata.linkedDbs.length > 1;
        const result = await updateBlueprintSettings({
          folderPath: editContext.blueprintFolderPath,
          label: hideLabelOnSubmit ? undefined : label.trim() || undefined,
          includedObjectTypes,
          // Only round-trip schema selection for engines that actually have
          // a schema concept; otherwise leave the persisted list untouched.
          includedSchemas: engineHasSchemas(source.engine)
            ? Array.from(selectedSchemas)
            : undefined,
          excludedObjects: nextExclusions,
          blueprintDataTables: Array.from(blueprintDataTables).map((k) => {
            const base = decodeTableKey(k);
            return blueprintDataDestructive.has(k)
              ? { ...base, allowDestructiveReplay: true as const }
              : base;
          }),
        });
        if (!result.success) {
          toast.error({
            title: 'Could not save settings',
            description: result.error,
          });
          return;
        }
        // Push the updated metadata back into the script-browser store so
        // the open Database Blueprint tab sees the new label / object types /
        // exclusions immediately.
        updateBlueprintMetadata(editContext.blueprintFolderId, {
          linkedDbs: result.metadata.linkedDbs.map((l) => ({
            label: l.label,
            connectionId: l.connectionId,
            connectionDisplayName: l.connectionDisplayName,
            database: l.database,
            engine: l.engine,
            lastSyncAt: l.lastSyncAt,
          })),
          excludedObjects: result.metadata.excludedObjects ?? [],
        });
        // Invalidate the blueprintStateStore so the toolbar reads the updated
        // blueprintDataTables on the next Refresh (Slice 10 / Fix 2).
        void useBlueprintStateStore
          .getState()
          .invalidate(editContext.blueprintFolderPath);
        // The IPC handler prunes stale `Data/*.data.sql` + `.data.json` files
        // when the user removes tables from the blueprint-data selection. Bump
        // the per-folder tree nonce so `MountedFolderTree` re-reads the
        // directory and the removed entries (and an emptied `Data/` folder)
        // disappear from the sidebar without a workspace reopen.
        useMountedFolderIndexStore
          .getState()
          .bumpFolderTreeNonce(editContext.blueprintFolderId);
        // Did this save WIDEN the blueprint's scope (new schemas, new object
        // types, lifted exclusions, or new blueprint-data tables)? If so, the
        // newly-in-scope objects only land on disk after a Refresh from DB,
        // so we prompt the user — without one the tree won't show them.
        // Note: we intentionally do NOT prompt for the inverse (removals)
        // because the removed-object pruning happens on the next refresh
        // and the user often hasn't decided whether to refresh yet.
        const prev = editContext.metadata;
        const addedSchemas = engineHasSchemas(source.engine)
          ? Array.from(selectedSchemas).some(
              (s) => !prev.includedSchemas.includes(s),
            )
          : false;
        const addedObjectTypes = (Object.keys(includedObjectTypes) as Array<
          keyof BlueprintObjectTypes
        >).some(
          (k) => includedObjectTypes[k] && !prev.includedObjectTypes[k],
        );
        const prevExclusionKeys = new Set(
          (prev.excludedObjects ?? []).map((e) =>
            `${e.kind}|${encodeTableKey({ schema: e.schema, name: e.name })}`,
          ),
        );
        const nextExclusionKeys = new Set(
          nextExclusions.map((e) =>
            `${e.kind}|${encodeTableKey({ schema: e.schema, name: e.name })}`,
          ),
        );
        const removedExclusions = [...prevExclusionKeys].some(
          (k) => !nextExclusionKeys.has(k),
        );
        const prevBlueprintDataKeys = new Set(
          (prev.blueprintDataTables ?? []).map((t) => encodeTableKey(t)),
        );
        const addedBlueprintDataTables = [...blueprintDataTables].some(
          (k) => !prevBlueprintDataKeys.has(k),
        );
        const widenedScope =
          addedSchemas ||
          addedObjectTypes ||
          removedExclusions ||
          addedBlueprintDataTables;

        if (widenedScope) {
          // Resolve the active link so the prompt's "Refresh from DB" action
          // can open the same dialog the toolbar opens.
          const activeLabel = resolveActiveLinkLabel(result.metadata) ?? '';
          const activeLink = result.metadata.linkedDbs.find(
            (l) => l.label === activeLabel,
          ) ?? result.metadata.linkedDbs[0];
          const folderId = editContext.blueprintFolderId;
          const folderPath = editContext.blueprintFolderPath;
          toast.success({
            title: 'Database Blueprint settings updated',
            description:
              'New objects are now in scope. Refresh from DB to fetch them.',
            ...(activeLink
              ? {
                  action: {
                    label: 'Refresh from DB',
                    onClick: () => {
                      useRefreshFromDbDialogStore.getState().openRefreshDialog({
                        folderPath,
                        folderId,
                        linkLabel: activeLink.label,
                        link: {
                          connectionDisplayName:
                            activeLink.connectionDisplayName,
                          database: activeLink.database,
                          engine: activeLink.engine,
                        },
                        blueprintDataTables:
                          result.metadata.blueprintDataTables ?? [],
                        activeLinkConnectionId: activeLink.connectionId,
                        onSuccess: (metadata) => {
                          updateBlueprintMetadata(folderId, {
                            linkedDbs: metadata.linkedDbs.map((l) => ({
                              label: l.label,
                              connectionId: l.connectionId,
                              connectionDisplayName: l.connectionDisplayName,
                              database: l.database,
                              engine: l.engine,
                              lastSyncAt: l.lastSyncAt,
                            })),
                          });
                          useMountedFolderIndexStore
                            .getState()
                            .bumpFolderTreeNonce(folderId);
                          toast.success({
                            title: 'Database Blueprint refreshed',
                            description: folderPath,
                          });
                        },
                      });
                    },
                  },
                }
              : {}),
          });
        } else {
          toast.success({ title: 'Database Blueprint settings updated' });
        }
        window.electronAPI?.analytics
          ?.track('feature:database_blueprint_settings_saved', {
            engine: source.engine,
          })
          .catch(() => {
            /* fire-and-forget */
          });
        closeSetupDialog();
      } catch (err) {
        toast.error({
          title: 'Could not save settings',
          description: err instanceof Error ? err.message : String(err),
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ---------- New-blueprint submit (progress-aware flow) ----------
    if (!folderPath) {
      setFolderError('Pick a folder first.');
      return;
    }
    // Finding #31 — final guard before the destructive write. Catches the
    // case where the user typed a non-empty folder path then submitted
    // without ever blurring the input (e.g. paste + Enter).
    const isEmpty = await validateFolderEmptiness(folderPath);
    if (!isEmpty) return;
    if (
      !canBlueprintLarge &&
      objectCount !== null &&
      objectCount > PERSONAL_MODE_MAX_OBJECTS
    ) {
      setShowUpgradeDialog(true);
      window.electronAPI?.analytics
        ?.track('feature:database_blueprint_blocked_by_limit', { object_count: objectCount })
        .catch(() => {
          /* fire-and-forget */
        });
      return;
    }

    // Materialise the picked exclusions into the BlueprintExcludedObject shape
    // before handing them to the setup IPC.
    const submittedExclusions: BlueprintExcludedObject[] = [];
    for (const row of allExclusionRows) {
      const key = exclusionRowKey(row);
      if (exclusionKeys.has(key)) {
        submittedExclusions.push({
          kind: row.kind as BlueprintExcludedObjectKind,
          schema: row.schema,
          name: row.name,
        });
      }
    }
    // Blueprints `markFolderAsBlueprint`'s previous fallback chain — the user can
    // leave the label field empty; we derive one from the database/engine.
    const effectiveLabel =
      label.trim() ||
      sanitizeDefaultBlueprintLabel(source.database) ||
      source.database ||
      source.engine;

    const operationId = generateOperationId();
    setSubmitting(true);
    setCreationProgress({
      ...INITIAL_CREATION_PROGRESS,
      open: true,
      status: 'running',
      phase: 'preparing',
      pendingLabel: effectiveLabel,
      operationId,
    });

    let result;
    try {
      result = await setupBlueprintWithProgress(
        {
          operationId,
          folderPath,
          connectionId: source.connectionId,
          database: source.database,
          engine: source.engine,
          connectionDisplayName: source.connectionName,
          label,
          includedObjectTypes,
          includedSchemas: Array.from(selectedSchemas),
          writeGitignore,
          excludedObjects: submittedExclusions,
          blueprintDataTables: Array.from(blueprintDataTables).map((k) => {
            const base = decodeTableKey(k);
            return blueprintDataDestructive.has(k)
              ? { ...base, allowDestructiveReplay: true as const }
              : base;
          }),
        },
        (evt) => {
          setCreationProgress((prev) => {
            if (!prev.open || prev.operationId !== evt.operationId) return prev;
            const nextEntry: BlueprintCreationLogEntry = {
              id: prev.log.length + 1,
              phase: evt.phase,
              message: evt.message,
            };
            return {
              ...prev,
              phase: evt.phase,
              currentIndex:
                evt.index !== undefined ? evt.index : prev.currentIndex,
              totalCount:
                evt.total !== undefined ? evt.total : prev.totalCount,
              log: [...prev.log, nextEntry],
            };
          });
        },
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setCreationProgress((prev) => ({
        ...prev,
        status: 'error',
        errorMessage: message,
        log: [
          ...prev.log,
          { id: prev.log.length + 1, phase: 'error', message },
        ],
      }));
      setSubmitting(false);
      return;
    }

    setSubmitting(false);

    if (result.success) {
      // Blueprint is on disk — register it in the script-browser store so it
      // shows up in the sidebar with the blueprint badge/tint. We do this on
      // success (not on the "Open Blueprint" click) because the folder really
      // exists at this point; the user just hasn't opened the workspace tab.
      //
      // M8: use the backend-returned link label (post `sanitizeBlueprintLabel`)
      // rather than the renderer-computed `effectiveLabel`. The renderer's
      // `sanitizeDefaultBlueprintLabel` is a near-blueprint of the backend's
      // sanitiser but not identical — a label that survived backend
      // sanitisation may differ from the renderer-computed one (different
      // character set, different collapsing rules). Always trust the
      // backend's authoritative label so the script-browser entry exactly
      // matches what was written to `jamsql-blueprint.json`.
      const backendLabel = result.metadata.linkedDbs[0]?.label ?? effectiveLabel;
      const folderId = useScriptBrowserStore.getState().markFolderAsBlueprint(
        result.folderPath,
        backendLabel,
        {
          version: 1,
          linkedDbs: result.metadata.linkedDbs.map((l) => ({
            label: l.label,
            connectionId: l.connectionId,
            connectionDisplayName: l.connectionDisplayName,
            database: l.database,
            engine: l.engine,
            lastSyncAt: l.lastSyncAt,
          })),
          excludedObjects: result.metadata.excludedObjects ?? [],
        }
      );
      setCreationProgress((prev) => ({
        ...prev,
        status: 'done',
        phase: 'done',
        summary: {
          folderPath: result.folderPath,
          counts: result.objectCounts,
        },
        pendingFolderId: folderId,
      }));
      window.electronAPI
        ?.analytics?.track('feature:database_blueprint_create', { engine: source.engine })
        .catch(() => {
          /* fire-and-forget */
        });
      // Task 5 — the schema emit succeeded, but blueprint-data capture may have
      // failed or skipped tables (e.g. no primary key). `runBlueprintSetup`
      // surfaces that additively as `dataWarning` on the success result. Fire a
      // non-fatal WARNING toast (not error — the blueprint itself was created)
      // so a user who picked data tables isn't left with a silent success and
      // zero `Data/` files. The progress dialog still proceeds as today.
      if (
        typeof result.dataWarning === 'string' &&
        result.dataWarning.trim().length > 0
      ) {
        toast.warning({
          title: 'Blueprint created — table data incomplete',
          description: result.dataWarning,
        });
      }
    } else if ('aborted' in result && result.aborted) {
      setCreationProgress((prev) => ({
        ...prev,
        status: 'aborted',
        phase: 'aborted',
        errorMessage: result.error,
      }));
      window.electronAPI
        ?.analytics?.track('feature:database_blueprint_create_aborted', {
          engine: source.engine,
        })
        .catch(() => {
          /* fire-and-forget */
        });
    } else if (isProRequiredError(result)) {
      // C4/C5 — when the IPC returns requires_pro, dispatch to the
      // UpgradeDialog and dismiss the progress overlay. Never display
      // the literal 'requires_pro' token to the user.
      setCreationProgress(INITIAL_CREATION_PROGRESS);
      window.electronAPI
        ?.analytics?.track('feature:database_blueprint_pro_gate_shown', {
          feature: 'database-blueprint-large',
        })
        .catch(() => { /* fire-and-forget */ });
      showUpgradePrompt(result.feature as import('../../../core/licensing/types').ProFeature);
    } else {
      setCreationProgress((prev) => ({
        ...prev,
        status: 'error',
        phase: 'error',
        errorMessage: result.error,
        log: [
          ...prev.log,
          { id: prev.log.length + 1, phase: 'error', message: result.error },
        ],
      }));
    }
  }, [
    closeSetupDialog,
    folderPath,
    includedObjectTypes,
    label,
    selectedSchemas,
    source,
    writeGitignore,
    canBlueprintLarge,
    objectCount,
    validateFolderEmptiness,
    isEditMode,
    showUpgradePrompt,
    editContext,
    exclusionKeys,
    allExclusionRows,
    blueprintDataTables,
    blueprintDataDestructive,
    updateBlueprintMetadata,
  ]);

  // Helpers for the compact summary rows (Create Database Blueprint / Database Blueprint settings
  // surface now collapses each multi-select to a one-line summary + "Change"
  // button to keep the dialog short). Declared before the `if (!source)` early
  // return so React's hook order stays consistent across renders.
  const objectTypesSummary = useMemo(() => {
    const entries = Object.entries(includedObjectTypes);
    const selectedLabels = entries
      .filter(([, v]) => v)
      .map(([k]) => OBJECT_TYPE_LABELS[k] ?? k);
    const unselectedLabels = entries
      .filter(([, v]) => !v)
      .map(([k]) => OBJECT_TYPE_LABELS[k] ?? k);
    return buildSelectionSummary({
      selectedLabels,
      totalCount: entries.length,
      entityNounSingular: 'object type',
      entityNounPlural: 'object types',
      allLabel: 'All object types',
      unselectedLabels,
    });
  }, [includedObjectTypes]);
  const schemasSummary = useMemo(() => {
    if (schemasLoading) return 'Loading schemas…';
    const total = availableSchemas.length;
    if (total === 0) return 'No schemas available';
    const selectedLabels = availableSchemas.filter((s) => selectedSchemas.has(s));
    const unselectedLabels = availableSchemas.filter((s) => !selectedSchemas.has(s));
    const base = buildSelectionSummary({
      selectedLabels,
      totalCount: total,
      entityNounSingular: 'schema',
      entityNounPlural: 'schemas',
      unselectedLabels,
    });
    return includeSystemSchemas
      ? `${base} · incl. system`
      : `${base} · user schemas only`;
  }, [
    schemasLoading,
    availableSchemas,
    selectedSchemas,
    includeSystemSchemas,
  ]);

  // H5 — multi-link edit mode hides the label input (the rename would
  // silently mutate linkedDbs[0]; the user can't tell which link gets it).
  // Single-link edit mode + new-blueprint mode keep the input.
  const labelInputHidden =
    isEditMode && editContext !== null && linkedDbsSnapshot.length > 1;

  if (!source) return null;

  const exceedsPersonalLimit =
    !canBlueprintLarge && objectCount !== null && objectCount > PERSONAL_MODE_MAX_OBJECTS;
  // All candidate tables (used to compute the unselected list for the
  // "All besides X" branch in the blueprint-data summary).
  const blueprintDataCandidates = allExclusionRows.filter((r) => r.kind === 'table');
  const blueprintDataCandidateLabels = blueprintDataCandidates.map((r) =>
    r.schema ? `${r.schema}.${r.name}` : r.name,
  );
  // For the summary builder we need human-readable labels for the selected set
  // and the unselected set — derive both from the candidate list so the labels
  // stay presentational (schema.name) while the Set membership test uses the
  // unambiguous encoded keys.
  const blueprintDataSelectedLabels = blueprintDataCandidates
    .filter((r) => blueprintDataTables.has(encodeTableKey(r)))
    .map((r) => (r.schema ? `${r.schema}.${r.name}` : r.name));
  const blueprintDataSummary = buildSelectionSummary({
    selectedLabels: blueprintDataSelectedLabels,
    totalCount: blueprintDataCandidateLabels.length,
    entityNounSingular: 'table',
    entityNounPlural: 'tables',
    emptyLabel: 'No tables — DDL only',
    allLabel:
      blueprintDataCandidateLabels.length > 0
        ? `All ${blueprintDataCandidateLabels.length} tables`
        : undefined,
    unselectedLabels: blueprintDataCandidates
      .filter((r) => !blueprintDataTables.has(encodeTableKey(r)))
      .map((r) => (r.schema ? `${r.schema}.${r.name}` : r.name)),
    // Allow up to 10 inline (vs the default 4 used by the other rows) since
    // the blueprint-data SummaryRow wraps instead of truncating. Beyond 10 we
    // fall through to "N of M tables" so the row stays compact.
    maxListed: 10,
  });

  // Build the small inline summary that sits next to the "Set exclusions…"
  // button. Exclusion semantics are inverted (selected = excluded), so the
  // "all selected" branch reads weirdly as "everything excluded" — we keep
  // the four-rule helper for counts but prefix the listed-items branches
  // with "Excluding" to make the meaning unambiguous.
  const exclusionSummaryText = (() => {
    if (exclusionKeys.size === 0) return 'No exclusions';
    const tickedLabels = allExclusionRows
      .filter((r) => exclusionKeys.has(exclusionRowKey(r)))
      .map((r) => (r.schema ? `${r.schema}.${r.name}` : r.name));
    const untickedLabels = allExclusionRows
      .filter((r) => !exclusionKeys.has(exclusionRowKey(r)))
      .map((r) => (r.schema ? `${r.schema}.${r.name}` : r.name));
    const raw = buildSelectionSummary({
      selectedLabels: tickedLabels,
      totalCount: allExclusionRows.length,
      entityNounSingular: 'object',
      entityNounPlural: 'objects',
      allLabel: `Excluding all ${allExclusionRows.length} objects`,
      unselectedLabels: untickedLabels,
    });
    // Rebrand "Selected: X, Y" → "Excluding X, Y" for clarity. "All besides X"
    // is left as-is (it already reads naturally — those are the only items
    // NOT excluded). The "N of M objects" fallback gets an "Excluded"
    // suffix-replacement so users don't read it as "5 of 11 included".
    if (raw.startsWith('Selected: ')) {
      return `Excluding ${raw.slice('Selected: '.length)}`;
    }
    if (/^\d+ of \d+ objects$/.test(raw)) {
      return `${raw.replace(/ objects$/, '')} objects excluded`;
    }
    return raw;
  })();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeSetupDialog()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="database-blueprint-setup-dialog"
        // Backdrop click + Escape must not dismiss — only the explicit Cancel
        // button closes the setup dialog. Mid-create dismissal would also
        // orphan in-flight progress dialogs.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          {/* Finding #22a — drop trailing ellipsis ("Create Database Blueprint…");
              the ellipsis signals "opens a dialog" on a trigger, not inside
              the opened dialog itself. The title flips to "Database Blueprint settings"
              when the dialog opens in edit mode from the toolbar Settings
              button. */}
          <div className="flex items-center gap-1.5">
            <DialogTitle>{isEditMode ? 'Database Blueprint settings' : 'Create Database Blueprint'}</DialogTitle>
            <DocsLinkButton
              url="https://jamsql.com/docs/database-blueprint/"
              doc="database-blueprint"
            />
          </div>
          <DialogDescription>
            {/* Finding #22b — use the engine's display name
                (e.g. "SQLite", "PostgreSQL") instead of the upper-cased id
                (e.g. "SQLITE", "PGSQL"). */}
            {isEditMode ? (
              <>
                Edit settings for the blueprint linked to {source.connectionName} ·{' '}
                {source.database} ({engineDisplayName(source.engine)}).
              </>
            ) : (
              <>
                Export {source.connectionName} · {source.database} as a folder of .sql files
                ({engineDisplayName(source.engine)}).
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Folder picker — only shown in the new-blueprint flow. In edit mode
              the folder path is already known and shouldn't be edited. */}
          {!isEditMode && (
            <div className="space-y-1">
              <Label htmlFor="database-blueprint-folder">Folder</Label>
              <div className="flex gap-2">
                <Input
                  id="database-blueprint-folder"
                  value={folderPath}
                  onChange={(e) => {
                    setFolderPath(e.target.value);
                    setFolderError(null);
                  }}
                  onBlur={handleFolderInputBlur}
                  placeholder="/path/to/your/blueprint-folder"
                  data-testid="database-blueprint-folder-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePickFolder}
                  data-testid="database-blueprint-pick-folder"
                >
                  <FolderOpen className="mr-1 h-4 w-4" /> Pick…
                </Button>
              </div>
              {folderError && (
                <Alert variant="destructive" className="mt-2" data-testid="database-blueprint-folder-error">
                  <AlertDescription>{folderError}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Label
              H5 — in edit mode the top-level label input renames
              `linkedDbs[0]` via the IPC handler. On multi-link blueprints that's
              ambiguous (the user can't tell which link will be renamed), so
              we hide the input entirely there — per-link rename will live in
              the Linked-databases picker in a follow-up. In single-link
              edit mode + new-blueprint mode the input stays and the surrounding
              label names the active link so the rename target is obvious. */}
          {!labelInputHidden && (
            <div className="space-y-1">
              <Label htmlFor="database-blueprint-label">
                {isEditMode && editContext && linkedDbsSnapshot.length === 1
                  ? `Link label — renames "${linkedDbsSnapshot[0].label}"`
                  : 'Label (for MetaInfo + Connections panel)'}
              </Label>
              <Input
                id="database-blueprint-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Dev, Stage, Prod"
                data-testid="database-blueprint-label-input"
              />
            </div>
          )}

          {/* Linked databases — edit mode only. Lets users add/remove links
              and flip the active link from the settings surface (the toolbar
              chip is the other entry point). */}
          {isEditMode && editContext && (
            <SummaryRow
              icon={<Link2 className="h-4 w-4 text-muted-foreground" />}
              title="Linked databases"
              summary={
                linkedDbsSnapshot.length === 1
                  ? '1 linked database'
                  : `${linkedDbsSnapshot.length} linked databases`
              }
              onChange={() => setLinkedDbsPickerOpen(true)}
              testId="database-blueprint-linked-dbs-row"
              disabled={submitting}
            />
          )}

          {/* Object types — collapsed to a one-line summary + Change button.
              The full checkbox list lives in ObjectTypesPickerDialog. */}
          <SummaryRow
            icon={<ListTree className="h-4 w-4 text-muted-foreground" />}
            title="Included object types"
            summary={objectTypesSummary}
            onChange={() => setObjectTypesPickerOpen(true)}
            testId="database-blueprint-object-types-row"
            disabled={submitting}
          />

          {/* Schemas — only relevant for engines with a real schema concept.
              SQLite/MySQL use database-as-schema, so the row would be inert. */}
          {engineHasSchemas(source.engine) && (
            <SummaryRow
              icon={<Database className="h-4 w-4 text-muted-foreground" />}
              title="Included schemas"
              summary={schemasSummary}
              onChange={() => setSchemasPickerOpen(true)}
              testId="database-blueprint-schemas-row"
              disabled={submitting}
            />
          )}

          {/* Set exclusions — moved up from the footer so all per-blueprint
              selectors live in the dialog body and the footer is just
              cancel/submit. */}
          <SummaryRow
            icon={<ListFilter className="h-4 w-4 text-muted-foreground" />}
            title="Excluded objects"
            summary={exclusionSummaryText}
            onChange={() => setExclusionPickerOpen(true)}
            testId="database-blueprint-setup-exclusions"
            changeLabel="Set exclusions…"
            disabled={submitting}
          />

          {/* Table data — opt-in per table. When selected, the emitter also
              writes a `.data.sql` upsert script alongside the DDL. The summary
              can list up to 10 table names, so we let it wrap instead of
              forcing it onto a single truncated line. */}
          <SummaryRow
            icon={<Table2 className="h-4 w-4 text-muted-foreground" />}
            title="Table data"
            summary={blueprintDataSummary}
            onChange={() => setBlueprintDataPickerOpen(true)}
            testId="database-blueprint-blueprint-data-row"
            changeLabel="Choose data tables…"
            disabled={submitting}
            wrapSummary
          />

          {/* .gitignore — only relevant on first setup. */}
          {!isEditMode && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="database-blueprint-write-gitignore"
                checked={writeGitignore}
                onCheckedChange={(v) => setWriteGitignore(v === true)}
                data-testid="database-blueprint-write-gitignore"
              />
              <Label htmlFor="database-blueprint-write-gitignore" className="text-sm">
                Add a default <code>.gitignore</code> (.DS_Store, editor noise)
              </Label>
            </div>
          )}
        </div>

        {isEditMode && blueprintDataTablesToDrop.length > 0 && (
          <Alert
            variant="default"
            className="border-amber-500/40 bg-amber-50/50 dark:bg-amber-900/10"
            data-testid="database-blueprint-blueprint-data-deletion-warning"
          >
            <AlertDescription className="flex items-start gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-warning shrink-0" />
              <span>
                Saving will <strong>delete</strong> the local{' '}
                <code className="text-[11px]">Data/</code> files (
                <code className="text-[11px]">.data.sql</code> +{' '}
                <code className="text-[11px]">.data.json</code>) for{' '}
                {blueprintDataTablesToDrop.length === 1 ? (
                  <>
                    <strong>
                      {blueprintDataTablesToDrop[0].schema
                        ? `${blueprintDataTablesToDrop[0].schema}.${blueprintDataTablesToDrop[0].name}`
                        : blueprintDataTablesToDrop[0].name}
                    </strong>
                  </>
                ) : blueprintDataTablesToDrop.length <= 5 ? (
                  <strong>
                    {blueprintDataTablesToDrop
                      .map((t) => (t.schema ? `${t.schema}.${t.name}` : t.name))
                      .join(', ')}
                  </strong>
                ) : (
                  <strong>{blueprintDataTablesToDrop.length} tables</strong>
                )}
                . Re-tick the tables and run Refresh from DB to regenerate
                the files from the source database.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {exceedsPersonalLimit && (
          <Alert
            variant="default"
            className="border-yellow-500/40 bg-yellow-50/50 dark:bg-yellow-900/10"
            data-testid="database-blueprint-personal-limit"
          >
            <AlertDescription className="flex items-start gap-2 text-sm">
              <Sparkles className="h-4 w-4 mt-0.5 text-yellow-600 dark:text-yellow-400" />
              <span>
                This database has <strong>{objectCount?.toLocaleString()}</strong> objects, which
                exceeds the Personal-mode Database Blueprint limit of{' '}
                {PERSONAL_MODE_MAX_OBJECTS.toLocaleString()}. Upgrade to Pro to blueprint larger
                databases, or narrow the schema selection.
              </span>
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter className="gap-2 items-center sm:justify-between">
          {!isEditMode ? (
            <div className="flex flex-col items-start">
              <Button
                type="button"
                variant="link"
                className="h-auto px-0 text-xs text-muted-foreground"
                onClick={handleAttachExisting}
                disabled={submitting}
                data-testid="database-blueprint-attach-existing"
              >
                Attach existing blueprint
              </Button>
              {attachError && (
                <span
                  className="text-xs text-destructive"
                  data-testid="database-blueprint-attach-error"
                >
                  {attachError}
                </span>
              )}
            </div>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeSetupDialog}
              disabled={submitting}
              data-testid="database-blueprint-cancel"
            >
              Cancel
            </Button>
            {exceedsPersonalLimit ? (
              <Button
                type="button"
                variant="default"
                onClick={() => setShowUpgradeDialog(true)}
                data-testid="database-blueprint-upgrade"
              >
                <Sparkles className="mr-1 h-4 w-4" /> Upgrade to Pro
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                // In edit mode the folder is fixed (already on disk), so the
                // folderPath check would always pass — and the folderError
                // can't be raised because the folder picker isn't rendered.
                disabled={
                  submitting ||
                  (!isEditMode && (!folderPath || !!folderError))
                }
                data-testid="database-blueprint-submit"
              >
                {isEditMode
                  ? submitting
                    ? 'Saving…'
                    : 'Save'
                  : submitting
                    ? 'Creating blueprint…'
                    : 'Create Blueprint'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
      <UpgradeDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog} />
      <ExclusionPickerDialog
        open={exclusionPickerOpen}
        onClose={() => setExclusionPickerOpen(false)}
        rows={allExclusionRows}
        excludedKeys={exclusionKeys}
        onChange={setExclusionKeys}
      />
      <ObjectTypesPickerDialog
        open={objectTypesPickerOpen}
        onClose={() => setObjectTypesPickerOpen(false)}
        engine={source.engine}
        value={includedObjectTypes}
        onChange={setIncludedObjectTypes}
      />
      <SchemasPickerDialog
        open={schemasPickerOpen}
        onClose={() => setSchemasPickerOpen(false)}
        availableSchemas={availableSchemas}
        selected={selectedSchemas}
        onChange={setSelectedSchemas}
        loading={schemasLoading}
        includeSystemSchemas={includeSystemSchemas}
        onIncludeSystemSchemasChange={setIncludeSystemSchemas}
      />
      <BlueprintDataPickerDialog
        open={blueprintDataPickerOpen}
        onClose={() => setBlueprintDataPickerOpen(false)}
        connectionId={source.connectionId}
        database={source.database}
        engine={source.engine}
        candidates={blueprintDataCandidateRows}
        // Tell the picker which empty-state to show when `candidates` is
        // empty. In edit mode the candidate list comes from disk, so the
        // "connect to a source DB first" prompt is wrong — surface the real
        // loader error (if any) or a folder-empty message instead.
        candidatesSource={source ? 'live-db' : 'blueprint-folder'}
        candidatesError={exclusionCandidatesError}
        selectedKeys={blueprintDataTables}
        onChange={setBlueprintDataTables}
        destructiveKeys={blueprintDataDestructive}
        onDestructiveChange={setBlueprintDataDestructive}
        onAddPrimaryKey={(row) => {
          window.electronAPI?.analytics
            ?.track('feature:database_blueprint_data_blueprint_add_pk_clicked', {
              engine: source.engine,
            })
            ?.catch(() => {
              /* fire-and-forget */
            });
          // Close both the picker and the setup dialog so the new Table
          // Designer tab is actually visible to the user — otherwise the
          // designer opens behind the modal and looks like nothing happened.
          setBlueprintDataPickerOpen(false);
          closeSetupDialog();
          void designTableAction.execute({
            connectionId: source.connectionId,
            databaseName: source.database,
            schemaName: row.schema,
            objectName: row.name,
            objectType: 'table',
            objectId: `${row.schema}.${row.name}`,
          });
        }}
      />
      {isEditMode && editContext && (
        <LinkedDatabasesPickerDialog
          open={linkedDbsPickerOpen}
          onOpenChange={setLinkedDbsPickerOpen}
          folderId={editContext.blueprintFolderId}
          folderPath={editContext.blueprintFolderPath}
          links={linkedDbsSnapshot}
          activeLinkLabel={activeLinkLabelSnapshot}
          onChange={async () => {
            // Re-read the on-disk metadata so the row count + active link
            // reflect adds/removes/switches the picker just made. The
            // picker mutates state via IPC + store; reading back from disk
            // keeps the dialog's snapshot honest even if the user takes
            // another action against the same fields.
            const result = await readBlueprintMetadata({
              folderPath: editContext.blueprintFolderPath,
            });
            if (result.success) {
              setLinkedDbsSnapshot(result.metadata.linkedDbs);
              setActiveLinkLabelSnapshot(
                resolveActiveLinkLabel(result.metadata) ?? '',
              );
            }
          }}
        />
      )}
      <BlueprintCreationProgressDialog
        open={creationProgress.open}
        status={creationProgress.status}
        phase={creationProgress.phase}
        log={creationProgress.log}
        currentIndex={creationProgress.currentIndex}
        totalCount={creationProgress.totalCount}
        summary={creationProgress.summary}
        errorMessage={creationProgress.errorMessage}
        onAbort={() => {
          const opId = creationProgress.operationId;
          if (!opId || creationProgress.status !== 'running') return;
          setCreationProgress((prev) => ({ ...prev, status: 'aborting' }));
          abortBlueprintSetup(opId).catch(() => {
            /* main process already finished — the result handler will resolve */
          });
        }}
        onClose={() => {
          // "Open Blueprint" path — done state → switch to the workspace and
          // dismiss both dialogs.
          if (
            creationProgress.status === 'done' &&
            creationProgress.pendingFolderId
          ) {
            openDatabaseBlueprintTab(
              creationProgress.pendingFolderId,
              creationProgress.pendingLabel,
            );
            setCreationProgress(INITIAL_CREATION_PROGRESS);
            closeSetupDialog();
            return;
          }
          // Aborted / error / closed-without-summary → dismiss only the
          // progress dialog and leave the setup form intact so the user can
          // adjust and retry.
          setCreationProgress(INITIAL_CREATION_PROGRESS);
        }}
      />
    </Dialog>
  );
}

/**
 * One-line summary row + "Change…" button. Used by the setup dialog to keep
 * the main form scannable — the actual multi-select UI lives in a sub-dialog.
 */
function SummaryRow({
  icon,
  title,
  summary,
  onChange,
  testId,
  changeLabel = 'Change…',
  disabled,
  wrapSummary,
}: {
  icon?: React.ReactNode;
  title: string;
  summary: string;
  onChange: () => void;
  testId: string;
  changeLabel?: string;
  disabled?: boolean;
  /**
   * When true the summary line wraps onto multiple lines instead of being
   * truncated with an ellipsis. Used by the Table data row, whose summary
   * can list up to 10 table names — a single-line truncate would either hide
   * most of them or force the dialog wider than its max width.
   */
  wrapSummary?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex justify-between gap-3 rounded-sm border bg-muted/20 px-3 py-2',
        wrapSummary ? 'items-start' : 'items-center',
      )}
      data-testid={testId}
    >
      <div
        className={cn(
          'flex gap-2 min-w-0',
          wrapSummary ? 'items-start' : 'items-center',
        )}
      >
        {icon}
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium">{title}</span>
          <span
            className={cn(
              'text-xs text-muted-foreground',
              wrapSummary ? 'whitespace-normal break-words' : 'truncate',
            )}
            data-testid={`${testId}-summary`}
          >
            {summary}
          </span>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onChange}
        disabled={disabled}
        className="shrink-0"
        data-testid={`${testId}-change`}
      >
        {changeLabel}
      </Button>
    </div>
  );
}
